import { useState, useCallback } from 'react';
import axios from 'axios';
import { Car, Loader2, ArrowRight, Check } from 'lucide-react';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import { API_URL } from '../../services/api';
import '../AllCss/SellACar.css';

const VIN_LENGTH = 17;

const CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent', desc: '(2% of cars KBB values) — Looks new and is in excellent mechanical condition' },
  { value: 'very_good', label: 'Very good', desc: '(28% of cars KBB values) — Has minor cosmetic defects and is in good mechanical condition' },
  { value: 'good', label: 'Good', desc: '(50% of cars KBB values) — Has repairable cosmetic defects and mechanical problems' },
  { value: 'fair', label: 'Fair', desc: '(20% of cars KBB values) — Requires some mechanical repairs' },
];

function formatDrive(drivetrain) {
  if (!drivetrain) return '—';
  const s = (drivetrain || '').toLowerCase();
  if (s.includes('all wheel')) return '4WD/AWD';
  if (s.includes('four wheel')) return '4WD';
  if (s.includes('front')) return 'FWD';
  if (s.includes('rear')) return 'RWD';
  return drivetrain;
}

function SellACar() {
  const [vin, setVin] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [decodedVehicle, setDecodedVehicle] = useState(null);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vinDecodeError, setVinDecodeError] = useState('');
  const [condition, setCondition] = useState('');

  const setZipCodeNormalized = useCallback((value) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    setZipCode(digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits);
  }, []);

  const handleLookUp = useCallback(async (e) => {
    e?.preventDefault();
    const v = (vin || '').trim().toUpperCase().replace(/\s/g, '');
    if (v.length !== 17) return;
    setVinDecodeError('');
    setVinDecoding(true);
    try {
      const res = await axios.get(`${API_URL}/api/offers/vin/decode`, {
        withCredentials: true, params: { vin: v } });
      if (res.data?.success && res.data?.data) {
        setDecodedVehicle(res.data.data);
        setCondition('');
      } else {
        setVinDecodeError(res.data?.message || "We couldn't find that VIN. Check the number and try again.");
      }
    } catch (err) {
      setVinDecodeError(err.response?.data?.message || err.message || "We couldn't find that VIN.");
    } finally {
      setVinDecoding(false);
    }
  }, [vin]);

  const handleChangeVehicle = useCallback(() => {
    setDecodedVehicle(null);
    setCondition('');
    setVinDecodeError('');
  }, []);

  return (
    <div className="sell-a-car-page">
      <Navbar />
      <section className="sell-a-car-hero">
        <div className="sell-a-car-inner">
          <h1 className="sell-a-car-title">Sell a car</h1>
          <p className="sell-a-car-subtitle">Enter your VIN to see your vehicle details and get an estimated offer.</p>

          {!decodedVehicle ? (
            <div className="sell-a-car-lookup">
              <input
                type="text"
                placeholder="Enter 17-character VIN"
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/gi, '').slice(0, VIN_LENGTH))}
                className={`sell-a-car-vin-input ${vinDecodeError ? 'sell-a-car-input-error' : ''}`}
                maxLength={VIN_LENGTH}
                autoComplete="off"
              />
              <input
                type="text"
                inputMode="numeric"
                placeholder="ZIP Code"
                value={zipCode}
                onChange={(e) => setZipCodeNormalized(e.target.value)}
                className="sell-a-car-zip-input"
                autoComplete="postal-code"
              />
              <button
                type="button"
                className="sell-a-car-lookup-btn"
                disabled={vinDecoding || (vin || '').trim().length !== 17}
                onClick={handleLookUp}
              >
                {vinDecoding ? (
                  <>
                    <Loader2 size={18} className="sell-a-car-spinner" aria-hidden />
                    Looking up…
                  </>
                ) : (
                  <>
                    Look up vehicle
                    <ArrowRight size={18} aria-hidden />
                  </>
                )}
              </button>
              {vinDecodeError && (
                <p className="sell-a-car-error" role="alert">{vinDecodeError}</p>
              )}
            </div>
          ) : (
            <div className="sell-a-car-two-cards">
              {/* Card 1: Vehicle information */}
              <div className="sell-a-car-card sell-a-car-card-vehicle">
                <h2 className="sell-a-car-card-title">Vehicle information</h2>
                <div className="sell-a-car-vehicle-image">
                  <Car size={72} strokeWidth={1.2} aria-hidden />
                </div>
                <p className="sell-a-car-ymm">
                  {[decodedVehicle.year, decodedVehicle.make, decodedVehicle.model].filter(Boolean).join(' ')}
                </p>
                <p className="sell-a-car-meta">
                  <span className="sell-a-car-meta-label">Drive</span>
                  <span>{formatDrive(decodedVehicle.drivetrain)}</span>
                </p>
                <p className="sell-a-car-meta">
                  <span className="sell-a-car-meta-label">Transmission</span>
                  <span>{(decodedVehicle.transmission || '—').replace(/^./, (c) => c.toUpperCase())}</span>
                </p>
                <p className="sell-a-car-meta">
                  <span className="sell-a-car-meta-label">VIN</span>
                  <span className="sell-a-car-vin-value">{decodedVehicle.vin || vin}</span>
                </p>
                <button type="button" className="sell-a-car-change-btn" onClick={handleChangeVehicle}>
                  Change vehicle
                </button>
              </div>

              {/* Card 2: Style and features → Condition */}
              <div className="sell-a-car-card sell-a-car-card-condition">
                <h2 className="sell-a-car-card-title sell-a-car-card-title-with-check">
                  <Check size={20} aria-hidden />
                  Style and features
                </h2>
                <div className="sell-a-car-condition-section">
                  <h3 className="sell-a-car-condition-heading">Condition</h3>
                  <p className="sell-a-car-condition-question">What is the overall condition of your vehicle?</p>
                  <p className="sell-a-car-condition-note">
                    This will help us provide you with a Kelley Blue Book® Trade-in Range to compare with our offer.
                  </p>
                  <a href="https://www.kbb.com/condition/" target="_blank" rel="noopener noreferrer" className="sell-a-car-kbb-link">
                    View full Kelley Blue Book® condition definitions
                  </a>
                  <div className="sell-a-car-condition-cards" role="radiogroup" aria-label="Vehicle condition">
                    {CONDITION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={condition === opt.value}
                        className={`sell-a-car-condition-card ${condition === opt.value ? 'sell-a-car-condition-card--selected' : ''}`}
                        onClick={() => setCondition(opt.value)}
                      >
                        {condition === opt.value && (
                          <span className="sell-a-car-condition-check" aria-hidden>
                            <Check size={18} />
                          </span>
                        )}
                        <span className="sell-a-car-condition-label">{opt.label}</span>
                        <span className="sell-a-car-condition-desc">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default SellACar;
